/**
 * BasePath - Renders a single path with optional events and custom rendering
 */

import React, { type ReactNode, useMemo } from 'react'
import type { Coordinate, Drawing } from '@/types'
import { buildSVGPath } from '@/utils/SvgPath.utils'
import { convertSVGEventToCoordinates } from '@/utils'
import { isEndPoint } from '@/utils/DrawingPoint.utils'
import { useTheme } from '@/contexts/SettingsContext'
import { getThemeAwareColor } from '@/utils/ColorUtils.ts'
import { FilledLineEnding } from './FilledLineEnding'
import {
	DASH_PATTERN_GAP_MULTIPLIER,
	DASH_PATTERN_LENGTH_MULTIPLIER,
	LINE_END_NONE,
} from '@/constants'

type BasePathProps = {
	drawing: Drawing
	transformPoint: (point: Coordinate) => Coordinate
	strokeScale?: number
	minStrokeWidth?: number

	// Optional events
	onClick?: () => void
	onContextMenu?: (position: Coordinate) => void
	onDragStart?: (position: Coordinate) => void
	onHover?: (isHovered: boolean) => void

	// Optional extras (selection glow, snap indicators)
	extras?: ReactNode

	// Style overrides
	strokeColorOverride?: string
	opacityOverride?: number
	filter?: string
	cursor?: string
}

export const BasePath = React.memo(function BasePath({
														 drawing,
														 transformPoint,
														 strokeScale = 1,
														 minStrokeWidth,
														 onClick,
														 onContextMenu,
														 onDragStart,
														 onHover,
														 extras,
														 strokeColorOverride,
														 opacityOverride,
														 filter,
														 cursor,
													 }: BasePathProps) {
	const { theme } = useTheme()

	const { d, startDirection, endDirection } = useMemo(() => {
		return buildSVGPath(drawing, transformPoint)
	}, [drawing, transformPoint])

	if (!d) return null

	const displayColor = strokeColorOverride ?? getThemeAwareColor(drawing.style.color, theme)
	const strokeWidth = Math.max(
		drawing.style.strokeWidth * strokeScale,
		minStrokeWidth ?? 0,
	)

	// Calculate dash pattern using constants
	const lineDash =
		drawing.style.lineStyle === 'dashed'
			? [
				strokeWidth * DASH_PATTERN_LENGTH_MULTIPLIER,
				strokeWidth * DASH_PATTERN_GAP_MULTIPLIER,
			]
			: []

	// Render line ending — place on the non-player end when linked to a player
	const playerLinkedToEnd = drawing.linkedPointId != null && isEndPoint(drawing, drawing.linkedPointId)
	const lineEndDirection = playerLinkedToEnd ? startDirection : endDirection
	const hasEnding = drawing.style.lineEnd !== LINE_END_NONE && lineEndDirection

	return (
		<g
			onClick={onClick}
			onMouseEnter={() => onHover?.(true)}
			onMouseLeave={() => onHover?.(false)}
		>
			{/* Custom extras (selection glow, filters, etc.) */}
			{extras}

			<g opacity={opacityOverride ?? 1}>
				{/* Main path */}
				<path
					d={d}
					fill="none"
					stroke={displayColor}
					strokeWidth={strokeWidth}
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeDasharray={lineDash.length > 0 ? lineDash.join(' ') : undefined}
					filter={filter}
					style={cursor ? { cursor } : undefined}
					pointerEvents={onClick || onContextMenu || onDragStart ? 'stroke' : 'none'}
					onContextMenu={(e) => {
						if (!onContextMenu) return
						e.preventDefault()
						e.stopPropagation()
						const coords = convertSVGEventToCoordinates(
							e,
							(e.currentTarget as SVGPathElement).ownerSVGElement,
						)
						if (!coords) return
						onContextMenu(coords.screen)
					}}
					onPointerDown={(e) => {
						if (!onDragStart) return
						const coords = convertSVGEventToCoordinates(
							e,
							(e.currentTarget as SVGPathElement).ownerSVGElement,
						)
						if (!coords) return
						onDragStart(coords.screen)
					}}
				/>

				{/* Filled line ending */}
				{hasEnding && (
					<FilledLineEnding
						lineEnd={drawing.style.lineEnd as 'arrow' | 'tShape'}
						point={lineEndDirection!.point}
						angle={lineEndDirection!.angle}
						strokeWidth={strokeWidth}
						color={displayColor}
					/>
				)}
			</g>
		</g>
	)
})
